import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Chip,
    IconButton,
    Tooltip,
    useTheme,
} from "@mui/material";
import {
    Calendar,
    MapPin,
    Edit,
    Trash2,
    Globe,
    Lock,
    Image as ImageIcon
} from 'lucide-react';
import { tokens } from "../../theme";
import { getMediaByEventId } from "./EventServices";

const AdminEventCard = ({ event, onEdit, onPublish, onDelete }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [mediaData, setMediaData] = useState([]);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (!event?.id) return;

        // We pass empty functions for placeholders as we just want the data
        getMediaByEventId(
            (data) => {
                setMediaData(data || []);
            },
            event.id,
            (err) => console.error(`Error fetching media for event ${event.id}:`, err),
            () => { }
        );
    }, [event.id]);

    // Use up to 3 images for the stack effect
    const safeMediaData = Array.isArray(mediaData) ? mediaData : [];
    const displayMedia = safeMediaData.length > 0 ? safeMediaData.slice(0, 3) : [];

    // Helper to get URL from the media object which might be direct or nested in sharedMedia
    const getMediaUrl = (mediaItem) => mediaItem?.sharedMedia?.mediaUrl || mediaItem?.mediaUrl;

    // Fallback to the event's imageUrl if no media found (or placeholder)
    const firstMediaUrl = displayMedia.length > 0 ? getMediaUrl(displayMedia[0]) : null;
    const mainImage = firstMediaUrl || event.imageUrl || "placeholder.jpg";

    return (
        <Card
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                borderRadius: "24px",
                backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#FFFFFF",
                border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible', // Allow stacked images to peek out if needed, but usually we clip content
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                    borderColor: colors.greenAccent[500],
                }
            }}
        >
            {/* Stacked Media Preview Area */}
            <Box sx={{ position: 'relative', height: 220, width: '100%', overflow: 'hidden', borderTopLeftRadius: '24px', borderTopRightRadius: '24px', bgcolor: colors.primary[500] }}>

                {/* Background Stacked Items */}
                {displayMedia.length > 1 && displayMedia.slice(1).map((media, index) => (
                    <Box
                        key={media.id}
                        component="img"
                        src={getMediaUrl(media)}
                        alt=""
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            opacity: 0.7,
                            transform: isHovered
                                ? `rotate(${(index + 1) * 5}deg) scale(0.95)`
                                : `rotate(${(index + 1) * 3}deg) scale(0.95)`,
                            transformOrigin: 'bottom center',
                            transition: 'transform 0.4s ease',
                            zIndex: 0
                        }}
                    />
                ))}

                {/* Main Image */}
                <Box
                    component="img"
                    src={mainImage}
                    alt={event.title}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 10,
                        transition: 'transform 0.4s ease',
                        transform: isHovered ? 'scale(1.05)' : 'scale(1)'
                    }}
                />

                {/* Overlay Gradient */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '40%',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                    zIndex: 11
                }} />

                <Chip
                    label={event.active ? "Published" : "Draft"}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        bgcolor: event.active ? colors.greenAccent[500] : "rgba(255,255,255,0.9)",
                        color: event.active ? (theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100]) : colors.grey[700],
                        fontWeight: 'bold',
                        backdropFilter: 'blur(4px)',
                        zIndex: 20,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                />

                {displayMedia.length > 1 && (
                    <Box sx={{ position: 'absolute', bottom: 12, right: 12, zIndex: 20, display: 'flex', gap: 0.5 }}>
                        <Chip
                            label={`+${mediaData.length - 1}`}
                            size="small"
                            icon={<ImageIcon size={12} />}
                            sx={{ bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', height: 24, paddingLeft: 1, backdropFilter: 'blur(4px)' }}
                        />
                    </Box>
                )}
            </Box>

            <CardContent sx={{ p: "24px", flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb="12px" sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    minHeight: '2.5em'
                }}>
                    {event.title}
                </Typography>

                <Box display="flex" flexDirection="column" gap="10px" mb="24px">
                    <Box display="flex" alignItems="center" gap="8px">
                        <Calendar size={16} color={colors.greenAccent[500]} />
                        <Typography variant="body2" color={colors.grey[300]}>
                            {new Date(event.eventDate || event.postedDate).toLocaleDateString()}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap="8px">
                        <MapPin size={16} color={colors.greenAccent[500]} />
                        <Typography variant="body2" color={colors.grey[300]} noWrap>{event.location || "Main Hall"}</Typography>
                    </Box>
                </Box>

                <Box display="flex" gap="10px" mt="auto">
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Edit size={18} />}
                        onClick={() => onEdit(event)}
                        sx={{
                            bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : colors.grey[200],
                            color: colors.grey[100],
                            boxShadow: "none",
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: "bold",
                            "&:hover": { bgcolor: colors.primary[300] }
                        }}
                    >
                        Edit
                    </Button>
                    <Tooltip title={event.active ? "Unpublish" : "Publish"}>
                        <IconButton
                            onClick={() => onPublish(event)}
                            sx={{
                                bgcolor: event.active ? `${colors.blueAccent[500]}1A` : `${colors.grey[500]}1A`,
                                color: event.active ? colors.blueAccent[500] : colors.grey[400],
                                borderRadius: "12px",
                                "&:hover": { bgcolor: event.active ? colors.blueAccent[500] : colors.grey[500], color: "#FFFFFF" }
                            }}
                        >
                            {event.active ? <Globe size={20} /> : <Lock size={20} />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Event">
                        <IconButton
                            onClick={() => onDelete(event)}
                            sx={{
                                bgcolor: `${colors.redAccent[500]}1A`,
                                color: colors.redAccent[500],
                                borderRadius: "12px",
                                "&:hover": { bgcolor: colors.redAccent[500], color: "#FFFFFF" }
                            }}
                        >
                            <Trash2 size={20} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    );
};

export default AdminEventCard;
